import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { DriverCatalog } from '@casa/driver-host';
import { tilesOf, type Dashboard } from '@casa/model';
import { AppForegroundContext, TileEnvironmentContext } from '@casa/tiles';
import { bridge } from '../bridge/native_bridge.ts';
import { DashboardError, onDashboardAnnounced, onReloadRequested, reloadDashboard, startingDashboard } from '../data/dashboard.ts';
import { goToSettings } from '../settings/settings_navigation.ts';
import { setSecretKeysOf } from '../data/cloud_secrets.ts';
import { dashboardSecrets, secretKeysOfDrivers } from '../data/dashboard_secrets.ts';
import { appLog } from '../diagnostics/log.ts';
import { LiveDataContext, startLiveData, type LiveData } from '../data/live_data.ts';
import { createTileEnvironment } from '../data/tile_environment.ts';
import { FrameOverlay } from '../diagnostics/frame_overlay.tsx';
import { loadDrivers } from '../drivers/drivers.ts';
import { FirstStartPage } from '../settings/first_start_page.tsx';
import { ConfirmDialog } from '../ui/confirm_dialog.tsx';
import { takeEditModeRequest, useEditor } from '../editing/editor.tsx';
import { useSaving } from '../editing/saving.tsx';
import { Alert, Drawer, HStack, IconButton as ChakraIconButton, Link, Portal, Stack, Text, useBreakpointValue, useMediaQuery } from '@chakra-ui/react';
import { IconGlyph } from '../ui/icon_glyph.tsx';
import { toaster } from '../ui/chakra.tsx';
import { hideSplash } from '../ui/splash.ts';
import { useAppUpdates } from '../updates/app_updates.ts';
import { MENU_BACKGROUND, NUMBER_LOCALE, PAGE_BACKGROUND } from '../ui/theme.ts';
import { ScreenDeck } from './screen_deck.tsx';
import { ScreenSwitcher } from './screen_switcher.tsx';
import { CORNER_CLEARANCE, MENU_BAR_HEIGHT, MENU_COLUMN_WIDTH, SideMenu, type MenuLayout } from './side_menu.tsx';
import { routeHref } from '../ui/route.ts';

/** What the tile views get from this app; the same object for the life of the page. */
const TILE_ENVIRONMENT = createTileEnvironment(bridge, NUMBER_LOCALE);

/**
 * Whether the app is in the foreground: the shell's `lifecycle` events, and the page's own
 * visibility (a hidden Electron window, a background browser tab).
 */
function useForeground() : boolean {
    const [shellForeground, setShellForeground] = useState(true);
    const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== 'hidden');

    useEffect(() => {
        const onVisibility = () : void => setPageVisible(document.visibilityState !== 'hidden');
        document.addEventListener('visibilitychange', onVisibility);
        const stopForeground = bridge?.lifecycle.onForeground(() => setShellForeground(true));
        const stopBackground = bridge?.lifecycle.onBackground(() => setShellForeground(false));

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            stopForeground?.();
            stopBackground?.();
        };
    }, []);

    return shellForeground && pageVisible;
}

const NO_CLOCK = { subscribe: () => () => {}, isOff: () => false, skewMs: () => undefined };

function describeSkew(skewMs: number) : string {
    const hours = Math.abs(skewMs) / 3_600_000;
    const amount = hours >= 48 ? `${Math.round(hours / 24)} days` : hours >= 1 ? `${Math.round(hours)} hours` : `${Math.round(hours * 60)} minutes`;

    return `${amount} ${skewMs > 0 ? 'behind' : 'ahead'}`;
}

/** A device clock far off breaks certificates and "today" values (Phase 1 C1); say so. */
function ClockBanner({ live }: { live: LiveData | null }) : ReactNode {
    const clock = live?.clock ?? NO_CLOCK;
    const off = useSyncExternalStore(clock.subscribe, clock.isOff);
    const skew = clock.skewMs();
    if (!off || skew === undefined) {
        return null;
    }

    return (
        // Below the status bar on iOS (see index.css); 0 elsewhere.
        <Alert.Root status="warning" variant="solid" position="fixed" top="var(--casa-top-inset)" left="0" right="0" borderRadius="0" zIndex="20">
            <Alert.Indicator />
            <Alert.Title>This device's clock is {describeSkew(skew)}. Secure connections and today's values fail until the date and time are set.</Alert.Title>
        </Alert.Root>
    );
}

/** How long a message stays on the screen. */
const NOTICE_MS = 8_000;
/** The phone's drawer: rows with each screen's icon and name (look LK-6). */
const MENU_PANEL_WIDTH = 240;
/** The bar above the screens on a phone, with the screen's name, the screen icons and the menu button. */
const TOP_BAR_HEIGHT = 52;

/**
 * What the drivers are started from: the connections and each tile's data sources. A change of
 * places or order in edit mode leaves it the same, so the data keeps running through a save.
 */
function sourcesKey(dashboard: Dashboard) : string {
    return JSON.stringify([dashboard.connections, dashboard.screens.flatMap(tilesOf).map((tile) => [tile.id, tile.dataSources])]);
}

type DashboardState =
    | { status: 'loading' }
    /** No dashboard on this device yet: the first start asks where it comes from. */
    | { status: 'setup' }
    | { status: 'loaded'; dashboard: Dashboard; drivers: DriverCatalog }
    | { status: 'failed'; error: string };

/**
 * The dashboard as the legacy `MainPage` shows it: the screens, and the menu beside them (a fixed
 * width), below them on a tablet in portrait (look LK-10), or in a drawer on a phone (LK-6).
 */
export function DashboardApp() : ReactNode {
    const [state, setState] = useState<DashboardState>({ status: 'loading' });
    const [live, setLive] = useState<LiveData | null>(null);
    // The splash screen stays until there is a dashboard, the first start's welcome, or an error.
    useEffect(() => {
        if (state.status !== 'loading') {
            hideSplash();
        }
    }, [state.status]);
    const [screenIndex, setScreenIndex] = useState(0);
    const foreground = useForeground();
    const [isAskingToCancel, setAskingToCancel] = useState(false);
    // Below Chakra's `md` breakpoint (a phone), the side menu is not shown beside the screens but opens
    // over them from a menu button in a bar at the top (the owner, 2026-10-16: shown all the time it
    // took too much room or was too small to use).
    const isNarrow = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;
    const [isPortrait = false] = useMediaQuery(['(orientation: portrait)'], { ssr: false });
    const menuLayout: MenuLayout = isNarrow ? 'drawer' : isPortrait ? 'bar' : 'column';
    const [isMenuOpen, setMenuOpen] = useState(false);
    /** A choice made in the phone menu, carried out once the menu has closed. */
    const afterMenu = useRef<(() => void) | undefined>(undefined);

    useEffect(() => {
        const shell = bridge;
        if (!shell) {
            setState({ status: 'failed', error: 'No bridge: the app runs in a shell (Electron, Android, iOS), or in a browser from the Tilez Hub.' });

            return;
        }

        const load = async () : Promise<void> => {
            try {
                const dashboard = await startingDashboard(shell);
                if (!dashboard) {
                    setState({ status: 'setup' });

                    return;
                }

                setState({ status: 'loaded', dashboard, drivers: await loadDrivers(shell) });
            } catch (error) {
                const issues = error instanceof DashboardError ? error.issues.map((issue) => `\n- ${issue}`).join('') : '';
                setState({ status: 'failed', error: `${error instanceof Error ? error.message : String(error)}${issues}` });
            }
        };
        void load();

        // A dashboard chosen or made in the settings, which lie over this one (docs/specs/settings.md, DB-2).
        return onDashboardAnnounced((dashboard) => {
            setScreenIndex(0);
            void loadDrivers(shell).then((drivers) => setState({ status: 'loaded', dashboard, drivers }));
        });
    }, []);

    /**
     * Reads the dashboard shown again from where it comes from (settings DB-8, issue #26), when the
     * settings ask, keeping the screen shown where it still has it. A source that fails leaves the
     * dashboard as it was.
     */
    const isReloading = useRef(false);
    const reload = async () : Promise<void> => {
        if (!bridge || isReloading.current) {
            return;
        }
        isReloading.current = true;
        try {
            const dashboard = await reloadDashboard(bridge);
            const drivers = await loadDrivers(bridge);
            setScreenIndex((index) => Math.min(index, Math.max(0, dashboard.screens.length - 1)));
            setState({ status: 'loaded', dashboard, drivers });
            toaster.create({ description: 'Reloaded the dashboard.', type: 'success', duration: NOTICE_MS });
        } catch (caught) {
            toaster.create({ description: `Not reloaded: ${caught instanceof Error ? caught.message : String(caught)}`, type: 'error', duration: NOTICE_MS });
        } finally {
            isReloading.current = false;
        }
    };
    useEffect(() => onReloadRequested(() => void reload()), []);

    const loaded = state.status === 'loaded' ? state : undefined;
    /** Edit mode's working copy (P-3): the screens show it; the stored dashboard changes only on Save. */
    const showSaved = (dashboard: Dashboard) : void => setState((current) => (current.status === 'loaded' ? { ...current, dashboard } : current));
    const saving = useSaving({
        bridge: bridge ?? undefined,
        onSaved: (dashboard, text) => {
            showSaved(dashboard);
            editor.end();
            toaster.create({ description: text, type: 'success', duration: NOTICE_MS });
        },
        onReloaded: (dashboard) => {
            showSaved(dashboard);
            editor.end();
            toaster.create({ description: 'Loaded the version from the Tilez Hub; the edits on this device are gone.', type: 'info', duration: NOTICE_MS });
        },
        onFailed: (text) => toaster.create({ description: text, type: 'error', duration: NOTICE_MS }),
    });
    const editor = useEditor(loaded?.dashboard, screenIndex, setScreenIndex, saving.saveAs);
    const draft = editor.draft;
    // The shell's updates: the page says it started, and a downloaded release takes over at a quiet moment (app-updates AU-25, AU-26).
    const notifyUpdated = useCallback((text: string) => toaster.create({ description: text, type: 'success', duration: NOTICE_MS }), []);
    useAppUpdates(bridge, draft !== null, notifyUpdated);
    const dashboard = draft?.dashboard ?? loaded?.dashboard;

    // A dashboard made on this device opens in edit mode (D-3).
    useEffect(() => {
        if (loaded && takeEditModeRequest()) {
            editor.begin();
        }
        // Once per dashboard that is loaded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded]);

    // The drivers run for the dashboard on the screen, the working copy too, so a copied tile shows
    // its data at once. They restart only when the connections or the tiles' sources change.
    const latestShown = useRef(dashboard);
    latestShown.current = dashboard;
    const liveKey = useMemo(() => (dashboard ? sourcesKey(dashboard) : undefined), [dashboard]);
    const drivers = loaded?.drivers;
    useEffect(() => {
        const current = latestShown.current;
        if (liveKey === undefined || !current || !drivers || !bridge) {
            return;
        }

        // Before any driver starts: its log lines may carry the credentials it was given.
        appLog.setSecrets(dashboardSecrets(current, drivers));
        // Saving to tilez-cloud seals the installed drivers' credential fields too (secrets SE-3).
        setSecretKeysOf(secretKeysOfDrivers(drivers));
        const started = startLiveData(current, bridge, drivers);
        setLive(started);

        return () => started.dispose();
    }, [liveKey, drivers]);

    useEffect(() => live?.setForeground(foreground), [live, foreground]);

    // The dashboard's default tile colours (T-2a), the working copy's while editing.
    const tileStyle = dashboard?.tileStyle;
    // A tile's tap to a screen (tiles spec TA-2, TA-5): the first screen of that name; none, nothing.
    const showScreen = useCallback((name: string) : void => {
        const index = latestShown.current?.screens.findIndex((screen) => screen.name === name) ?? -1;
        if (index >= 0) {
            setScreenIndex(index);
        }
    }, []);
    const environment = useMemo(() => ({ ...TILE_ENVIRONMENT, ...(tileStyle ? { tileStyle } : {}), showScreen }), [tileStyle, showScreen]);

    const shownScreen = dashboard?.screens[screenIndex];
    useEffect(() => {
        if (live && shownScreen) {
            live.showScreen(shownScreen);
        }
    }, [live, shownScreen]);

    if (state.status === 'failed') {
        return (
            <Stack className="chrome" p="4" gap="3" maxW="3xl">
                <Alert.Root status="error">
                    <Alert.Indicator />
                    <Alert.Description whiteSpace="pre-wrap">{state.error}</Alert.Description>
                </Alert.Root>
                <Link href={routeHref('/settings/dashboards')} colorPalette="blue" onClick={(event) => {
                    event.preventDefault();
                    goToSettings('dashboards');
                }}>
                    Load it again, or choose another →
                </Link>
            </Stack>
        );
    }
    if (state.status === 'setup' && bridge) {
        const shell = bridge;

        return (
            <FirstStartPage shell={shell} />
        );
    }
    // Setup needs the bridge, and without it the state is `failed`.
    if (state.status === 'loading' || state.status === 'setup') {
        return null;
    }

    const saved = state.dashboard;
    const { screens, grid, background } = draft?.dashboard ?? saved;
    const isEditing = draft !== null;
    const hasChanges = isEditing && draft.dashboard !== saved;

    const leaveEditMode = () : void => {
        editor.end();
        setAskingToCancel(false);
    };

    const save = async () : Promise<void> => {
        if (!draft) {
            return;
        }
        if (!hasChanges) {
            leaveEditMode();

            return;
        }

        await saving.save(draft);
    };

    /**
     * On a phone the menu is a drawer: a choice in it closes the drawer first and is carried out once
     * it has gone. A dialog opened while the drawer still closes would take the focus the drawer gives
     * back for a tap beside it, and close at once.
     */
    const fromMenu = (action: () => void) : (() => void) =>
        isNarrow
            ? () => {
                afterMenu.current = action;
                setMenuOpen(false);
            }
            : action;
    const selectScreen = (index: number) : void => {
        editor.screenEditing?.onSelect(undefined);
        setScreenIndex(index);
    };
    const sideMenu = (
        <SideMenu
            screens={screens}
            current={screenIndex}
            onSelectScreen={(index) => fromMenu(() => selectScreen(index))()}
            layout={menuLayout}
            isEditing={isEditing}
            hasChanges={hasChanges}
            onSettings={fromMenu(() => goToSettings())}
            onEdit={fromMenu(editor.begin)}
            onAddTile={fromMenu(editor.openTilePicker)}
            onScreens={fromMenu(editor.openScreens)}
            onConnections={fromMenu(editor.openConnections)}
            onColors={fromMenu(editor.openColors)}
            onCancel={fromMenu(() => (hasChanges ? setAskingToCancel(true) : leaveEditMode()))}
            onSave={fromMenu(() => void save())}
        />
    );

    return (
        <LiveDataContext.Provider value={live}>
            <TileEnvironmentContext.Provider value={environment}>
                <AppForegroundContext.Provider value={foreground}>
                    {/* Nothing is selected by a hold, nor gets iOS's menu: WebKit takes only its own names (the Mac's light tile results, note 1). */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: menuLayout === 'column' ? 'row' : 'column',
                            height: '100%',
                            background: PAGE_BACKGROUND,
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            WebkitTouchCallout: 'none',
                        }}
                    >
                        {isNarrow && (
                            // On a phone, a bar above the screens with the menu button, so that the button
                            // covers no tile (the owner's manual test, 2026-10-16).
                            <HStack className="chrome" flex="none" height={`${TOP_BAR_HEIGHT}px`} gap="1" pl="3" pr="1" bg={MENU_BACKGROUND}>
                                <Text flex="1" minW="0" truncate fontSize="lg" color="white">
                                    {screens[screenIndex]?.name}
                                    {isEditing ? (
                                        <Text as="span" color={hasChanges ? 'orange.400' : 'fg.muted'}>
                                            {' '}
                                            · editing
                                        </Text>
                                    ) : null}
                                </Text>
                                <ScreenSwitcher screens={screens} current={screenIndex} onSelectScreen={selectScreen} onMore={() => setMenuOpen(true)} />
                                <ChakraIconButton aria-label="Menu" variant="ghost" onClick={() => setMenuOpen(true)}>
                                    <IconGlyph name="bars" size={22} />
                                </ChakraIconButton>
                            </HStack>
                        )}
                        <div style={{ flex: 1, position: 'relative', minWidth: 0, minHeight: 0 }}>
                            <ScreenDeck
                                screens={screens}
                                grid={grid}
                                current={screenIndex}
                                background={background}
                                editing={editor.screenEditing}
                            />
                        </div>
                        {menuLayout === 'column' && <div style={{ flex: 'none', width: `calc(${MENU_COLUMN_WIDTH}px + ${CORNER_CLEARANCE})` }}>{sideMenu}</div>}
                        {menuLayout === 'bar' && <div style={{ flex: 'none', height: `calc(${MENU_BAR_HEIGHT}px + env(safe-area-inset-bottom))` }}>{sideMenu}</div>}
                    </div>
                    {isNarrow && (
                        <Drawer.Root
                            open={isMenuOpen}
                            placement="end"
                            onOpenChange={(details) => setMenuOpen(details.open)}
                            onExitComplete={() => {
                                const action = afterMenu.current;
                                afterMenu.current = undefined;
                                action?.();
                            }}
                        >
                            <Portal>
                                <Drawer.Backdrop />
                                <Drawer.Positioner className="chrome">
                                    <Drawer.Content
                                        width={`${MENU_PANEL_WIDTH}px`}
                                        maxWidth="80vw"
                                        bg={MENU_BACKGROUND}
                                        pt="var(--casa-top-inset)"
                                        pb="env(safe-area-inset-bottom)"
                                    >
                                        {sideMenu}
                                    </Drawer.Content>
                                </Drawer.Positioner>
                            </Portal>
                        </Drawer.Root>
                    )}
                    {isAskingToCancel && (
                        <ConfirmDialog
                            message="Throw away the changes made since editing started?"
                            confirm="Throw away"
                            cancel="Keep editing"
                            onConfirm={leaveEditMode}
                            onCancel={() => setAskingToCancel(false)}
                        />
                    )}
                    {editor.overlay}
                    {saving.overlay}
                    <ClockBanner live={live} />
                    <FrameOverlay />
                </AppForegroundContext.Provider>
            </TileEnvironmentContext.Provider>
        </LiveDataContext.Provider>
    );
}
